import React from 'react';
import Config from '../../../Config';
import OneBannerRight from './layouts/OneBannerRight';
import OneBannerCentered from './layouts/OneBannerCentered';
import TwoBannersCentered from './layouts/TwoBannersCentered';
import TwoBannersTwoThirds from './layouts/TwoBannersTwoThirds';
import MobileOneBanner from './layouts/MobileOneBanner';
import MobileTwoBanners from './layouts/MobileTwoBanners';
import './MarketingHomeBannerBlock.scss';

const MarketingHomeBannerBlock = ({context, iContent}) => {
  // Extract both desktop and mobile layouts
  const desktopLayout = iContent.desktopLayout?.value;
  const mobileLayout = iContent.mobileLayout?.value;

  // Helper function to get media URL
  const getMediaUrl = (contentReference) => {
    if (!contentReference?.value) return null;
    const url = contentReference.value?.url || contentReference.expandedValue?.url;
    return url ? Config.epiBaseUrl + url : null;
  };

  // Helper function to detect media type
  const getMediaType = (contentReference) => {
    if (!contentReference?.value) return null;
    const contentType = contentReference.expandedValue?.contentType?.[0] || '';
    const url = contentReference.value?.url || contentReference.expandedValue?.url || '';

    if (contentType.toLowerCase().includes('video') || url.toLowerCase().endsWith('.mp4')) {
      return 'video';
    }
    if (url.toLowerCase().endsWith('.gif')) {
      return 'gif';
    }
    return 'image';
  };

  // Extract desktop data
  const extractDesktopData = () => ({
    // Main Banner
    mainBackground: getMediaUrl(iContent.desktopMainBackground),
    mainBackgroundType: getMediaType(iContent.desktopMainBackground),
    mainBackgroundColor: iContent.desktopMainBackgroundColor?.value,
    mainText: iContent.desktopMainText?.value,
    mainTextColor: iContent.desktopMainTextColor?.value,
    additionalText: iContent.desktopAdditionalText?.value,
    additionalTextColor: iContent.desktopAdditionalTextColor?.value,
    secondaryText: iContent.desktopSecondaryText?.value,
    secondaryTextColor: iContent.desktopSecondaryTextColor?.value,
    // Main Buttons
    mainButton: {
      text: iContent.desktopMainButtonText?.value,
      link: iContent.desktopMainButtonLink?.value,
      style: iContent.desktopMainButtonStyle?.value || 'default',
    },
    secondaryButton: {
      text: iContent.desktopSecondaryButtonText?.value,
      link: iContent.desktopSecondaryButtonLink?.value,
      style: iContent.desktopSecondaryButtonStyle?.value || 'outline-white',
    },
    // Secondary Banner
    secondaryBanner: {
      background: getMediaUrl(iContent.desktopSecondaryBackground),
      backgroundType: getMediaType(iContent.desktopSecondaryBackground),
      backgroundColor: iContent.desktopSecondaryBackgroundColor?.value,
      mainText: iContent.desktopSecondaryBannerMainText?.value,
      mainTextColor: iContent.desktopSecondaryBannerMainTextColor?.value,
      additionalText: iContent.desktopSecondaryBannerAdditionalText?.value,
      additionalTextColor: iContent.desktopSecondaryBannerAdditionalTextColor?.value,
      secondaryText: iContent.desktopSecondaryBannerSecondaryText?.value,
      secondaryTextColor: iContent.desktopSecondaryBannerSecondaryTextColor?.value,
      button: {
        text: iContent.desktopSecondaryBannerButtonText?.value,
        link: iContent.desktopSecondaryBannerButtonLink?.value,
        style: iContent.desktopSecondaryBannerButtonStyle?.value || 'default',
      },
    },
  });

  // Extract mobile data
  const extractMobileData = () => ({
    // Main Banner
    mainBackground: getMediaUrl(iContent.mobileMainBackground),
    mainBackgroundType: getMediaType(iContent.mobileMainBackground),
    mainBackgroundColor: iContent.mobileMainBackgroundColor?.value,
    mainText: iContent.mobileMainText?.value,
    mainTextColor: iContent.mobileMainTextColor?.value,
    additionalText: iContent.mobileAdditionalText?.value,
    additionalTextColor: iContent.mobileAdditionalTextColor?.value,
    secondaryText: iContent.mobileSecondaryText?.value,
    secondaryTextColor: iContent.mobileSecondaryTextColor?.value,
    // Main Buttons
    mainButton: {
      text: iContent.mobileMainButtonText?.value,
      link: iContent.mobileMainButtonLink?.value,
      style: iContent.mobileMainButtonStyle?.value || 'default',
    },
    secondaryButton: {
      text: iContent.mobileSecondaryButtonText?.value,
      link: iContent.mobileSecondaryButtonLink?.value,
      style: iContent.mobileSecondaryButtonStyle?.value || 'outline-white',
    },
    // Secondary Banner
    secondaryBanner: {
      background: getMediaUrl(iContent.mobileSecondaryBackground),
      backgroundType: getMediaType(iContent.mobileSecondaryBackground),
      backgroundColor: iContent.mobileSecondaryBackgroundColor?.value,
      mainText: iContent.mobileSecondaryBannerMainText?.value,
      mainTextColor: iContent.mobileSecondaryBannerMainTextColor?.value,
      additionalText: iContent.mobileSecondaryBannerAdditionalText?.value,
      additionalTextColor: iContent.mobileSecondaryBannerAdditionalTextColor?.value,
      secondaryText: iContent.mobileSecondaryBannerSecondaryText?.value,
      secondaryTextColor: iContent.mobileSecondaryBannerSecondaryTextColor?.value,
      button: {
        text: iContent.mobileSecondaryBannerButtonText?.value,
        link: iContent.mobileSecondaryBannerButtonLink?.value,
        style: iContent.mobileSecondaryBannerButtonStyle?.value || 'default',
      },
    },
  });

  const desktopData = extractDesktopData();
  const mobileData = extractMobileData();

  // Render desktop layouts
  const renderDesktopLayout = () => {
    switch (desktopLayout) {
      case 'ONE_RIGHT':
        return <OneBannerRight data={desktopData} context={context} />;
      case 'ONE_CENTERED':
        return <OneBannerCentered data={desktopData} context={context} />;
      case 'TWO_CENTERED':
        return <TwoBannersCentered data={desktopData} context={context} />;
      case 'TWO_THIRDS':
        return <TwoBannersTwoThirds data={desktopData} context={context} />;
      default:
        return <OneBannerCentered data={desktopData} context={context} />;
    }
  };

  // Render mobile layouts
  const renderMobileLayout = () => {
    switch (mobileLayout) {
      case 'ONE_BANNER':
        return <MobileOneBanner data={mobileData} context={context} />;
      case 'TWO_BANNERS':
        return <MobileTwoBanners data={mobileData} context={context} />;
      default:
        return <MobileOneBanner data={mobileData} context={context} />;
    }
  };

  return (
    <div className="marketing-home-banner">
      <div className="marketing-home-banner__desktop">
        {renderDesktopLayout()}
      </div>
      <div className="marketing-home-banner__mobile">
        {renderMobileLayout()}
      </div>
    </div>
  );
};

export default MarketingHomeBannerBlock;
